import { Component, Input, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';

import { MatDialogRef } from '@angular/material/dialog';
import {
  BehaviorSubject,
  Observable,
  Subject,
  catchError,
  debounceTime,
  finalize,
  of,
  startWith,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { BackendAccessService } from '../../../Services/APICalls/backend-access-service';
import { LoadingService } from '../../../Services/Loading/loading-service.service';
import { OnboardingCompanySearch } from '../../../interfaces/company-search.interface';
import { UpdateOnboardingDialogComponent } from '../update-onboarding-dialog/update-onboarding-dialog.component';
import { AuthService } from '../../../Services/Auth/auth.service';
import {
  CredentialPricingItem,
  CredentialPricingTable,
  CredentialPriceRow,
} from '../../../interfaces/credential-pricing.interface';

@Component({
  selector: 'app-onboarding',
  templateUrl: './onboarding.component.html',
  styleUrls: ['./onboarding.component.scss'],
  standalone: false,
})
export class OnboardingComponent implements OnInit {
  onboardingForm!: FormGroup;
  accounts: any[] = [];
  filteredOptions!: Observable<OnboardingCompanySearch[]>;
  companySearchControl = new FormControl();
  options: OnboardingCompanySearch[] = [];
  @Input() itemData: any;
  @Input() dialogRef!: MatDialogRef<UpdateOnboardingDialogComponent>;
  isOnboardingExternalUser!: boolean;
  isOnboardingInternalUser!: boolean;
  isExternalUser!: boolean;
  isInternalUser!: boolean;
  private readonly unsubscribe$ = new Subject<void>();
  apiErrorMessage: string | null = null;
  credentialPricing$ = new BehaviorSubject<CredentialPricingTable | null>(null);
  selectedAccountSubType: string | null = null;

  constructor(
    private readonly fb: FormBuilder,
    public backend: BackendAccessService,
    private readonly loadingService: LoadingService,
    public snackBar: MatSnackBar,
    private readonly auth: AuthService
  ) {}
  /* istanbul ignore next */
  ngOnInit(): void {
    this.auth.userProfile$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((userProfile) => {
        console.log(userProfile, 'userProfile-credential-library');

        if (userProfile) {
          // Check if the user is internal
          this.isInternalUser =
            userProfile['http://allegion.com/claims/user_type'] === 'internal';
          this.isExternalUser =
            userProfile['http://allegion.com/claims/user_type'] === 'external';

          // Now, separately determine if they are a TechSupport role; this is irrespective of being internal or external
          const roles = userProfile['http://allegion.com/claims/roles'] || [];
          this.isOnboardingInternalUser =
            this.isInternalUser &&
            (roles.includes('Onboarding') || roles.includes('InternalAdmin'));
          this.isOnboardingExternalUser =
            this.isExternalUser &&
            (roles.includes('Onboarding') || roles.includes('InternalAdmin'));
        }
      });

    this.backend
      .getCredentialPricing()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (items) => this.credentialPricing$.next(this.pivotPricing(items)),
        error: () => {},
      });

    this.onboardingForm = this.fb.group({
      accountId: ['', Validators.required],
      product: [null, Validators.required],
      integrationName: [''],
      integrationId: [''],
      anniversaryDate: [null],
      notes: [''],
      isLiveClientSite: [true],
      invoiceToCustomer: [true],
      initialPoRequired: [true],
    });

    const updateValidators = () => {
      if (!this.onboardingForm || !this.onboardingForm.controls) {
        return;
      }

      const commonValidators = [Validators.required];

      // Clearing validators first to ensure a clean state
      this.onboardingForm.controls['integrationName'].clearValidators();
      this.onboardingForm.controls['integrationId'].clearValidators();
      this.onboardingForm.controls['anniversaryDate'].clearValidators();

      switch (this.onboardingForm.get('product')?.value) {
        case 2: // API Credentials
          this.onboardingForm.controls['integrationName'].setValidators(
            commonValidators
          );
          this.onboardingForm.controls['integrationId'].setValidators(
            commonValidators
          );
          this.onboardingForm.controls['anniversaryDate'].setValidators(
            Validators.required
          );
          break;
        case 3: // EB
        case 4: // SI
        case 5: // RK
          // Common settings for EB, SI, and RK where anniversaryDate is optional.
          this.onboardingForm.controls['integrationName'].setValidators(
            commonValidators
          );
          this.onboardingForm.controls['integrationId'].setValidators(
            commonValidators
          );
          this.onboardingForm.controls['anniversaryDate'].clearValidators();
          // anniversaryDate validators are already cleared before the switch.
          break;
        default:
          // No validators needed for the default case, already cleared at the start.
          break;
      }

      // Update the form controls' validity status.
      this.onboardingForm.controls['integrationName'].updateValueAndValidity();
      this.onboardingForm.controls['integrationId'].updateValueAndValidity();
      this.onboardingForm.controls['anniversaryDate'].updateValueAndValidity();
    };

    // Call updateValidators() to set the initial validators
    updateValidators();

    // Listen to radioButtonSelection value changes and update the validators accordingly
    this.onboardingForm.get('product')?.valueChanges.subscribe(() => {
      updateValidators();
    });

    // Clear API error when form values change
    this.onboardingForm.valueChanges.subscribe(() => {
      this.apiErrorMessage = null;
    });

    this.filteredOptions = this.companySearchControl.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      switchMap((value) => this.getAccounts(value))
    );
    // If itemData is provided (e.g., when opening this component from a dialog with data), populate the form
    if (this.itemData) {
      console.log('Item Data:', this.itemData);
      this.populateFormWithData(this.itemData);
      this.companySearchControl.setValue({
        accountName: this.itemData.accountName,
        accountNumber: this.itemData.accountNumber,
        accountId: this.itemData.accountId,
      });
      this.selectedAccountSubType = this.itemData.accountSubType ?? null;
    }
  }
  /* istanbul ignore next */
  populateFormWithData(data: any): void {
    console.log('Populating form with data:', data);
    this.onboardingForm.patchValue({
      accountId: data.accountNumber || '',

      product: data.mappingType || '',
      integrationName: data.integrationName || '',
      integrationId: data.integrationId || '',
      anniversaryDate: data.anniversaryDate
        ? new Date(data.anniversaryDate)
        : null,
      notes: data.notes || '',
      isLiveClientSite:
        data.isLiveClientSite !== undefined ? data.isLiveClientSite : true,
      invoiceToCustomer:
        data.invoiceToCustomer !== undefined ? data.invoiceToCustomer : true,
      initialPoRequired:
        data.initialPoRequired !== undefined ? data.initialPoRequired : true,
    });
  }

  /* istanbul ignore next */
  getAccounts(value: string): Observable<any[]> {
    if (!value) {
      return of([]); // Don't call the API if the input is empty
    }

    return this.backend.getOnboardingAccountsSearch(value).pipe(
      tap((results: OnboardingCompanySearch[]) => {
        this.accounts = results;
        console.log('Accounts:', this.accounts);
      }),
      catchError((error) => {
        this.loadingService.hide();
        return of([]);
      }),
      finalize(() => this.loadingService.hide())
    );
  }

  /* istanbul ignore next */
  displayAccountName(account: OnboardingCompanySearch | null): string {
    if (account) {
      this.onboardingForm.controls['accountId'].setValue(account.accountId);

      // Check if accountName exists, otherwise use businessId
      const nameOrId = account.accountName ?? account.businessId ?? 'N/A';

      return `${nameOrId} (Account #${account.accountNumber || 'N/A'})`;
    } else {
      this.onboardingForm.controls['accountId'].setValue('');
      return '';
    }
  }
  /* istanbul ignore next */
  submitOnboardingForm(): void {
    if (this.itemData && this.itemData.accountSiteMappingId) {
      const formValues = this.onboardingForm.value;
      const accountSiteMappingId = this.itemData.accountSiteMappingId;

      const patchData = [
        {
          op: 'replace',
          path: '/integrationName',
          value: formValues.integrationName,
        },
        {
          op: 'replace',
          path: '/integrationId',
          value: formValues.integrationId,
        },
        {
          op: 'replace',
          path: '/product',
          value: formValues.product,
        },
        {
          op: 'replace',
          path: '/anniversaryDate',
          value: formValues.anniversaryDate,
        },
        {
          op: 'replace',
          path: '/notes',
          value: formValues.notes,
        },
        {
          op: 'replace',
          path: '/accountId',
          value: formValues.accountId,
        },
        {
          op: 'replace',
          path: '/isLiveClientSite',
          value: formValues.isLiveClientSite,
        },
        {
          op: 'replace',
          path: '/invoiceToCustomer',
          value: formValues.invoiceToCustomer,
        },
        {
          op: 'replace',
          path: '/initialPoRequired',
          value: formValues.initialPoRequired,
        },
      ];

      this.backend
        .updateOnboardingData(accountSiteMappingId, patchData)
        .subscribe({
          next: (response) => {
            if (this.dialogRef) {
              this.dialogRef.close(true);
            }
            this.apiErrorMessage = null;
            console.log('Form updated successfully:');
            this.snackBar.open('Form updated successfully!', 'Close', {
              duration: 1000,
            });
          },
          error: (error) => {
            console.error('Error updating form:', error);
            this.apiErrorMessage =
              error?.error?.message ||
              error?.message ||
              'An error occurred while updating the form. Please try again.';
          },
        });
    } else {
      // Handle the creation of a new entry if we're not updating
      const formValue = this.onboardingForm.value;

      this.backend.submitOnboardingData(formValue).subscribe({
        next: (response) => {
          console.log('Form submitted successfully:', response);
          this.apiErrorMessage = null;
          this.snackBar.open('Form submitted successfully!', 'Close', {
            duration: 7000,
          });
          // Reset the form only if submission was successful
          this.clearForm();
        },
        error: (error) => {
          console.error('Error submitting form:', error);
          this.apiErrorMessage =
            error?.error?.message ||
            error?.message ||
            'An error occurred while submitting the form. Please try again.';
        },
      });
    }
  }

  /* istanbul ignore next */
  clearForm(): void {
    this.onboardingForm.reset({
      accountId: '',
      product: null,
      integrationName: '',
      integrationId: '',
      anniversaryDate: null,
      notes: '',
      isLiveClientSite: true,
      invoiceToCustomer: true,
      initialPoRequired: true,
    });
    this.companySearchControl.reset();
    this.apiErrorMessage = null;
  }

  /* istanbul ignore next */
  private pivotPricing(items: CredentialPricingItem[]): CredentialPricingTable {
    const preferredOrder = [
      'Third Party Agency',
      'Internal',
      'Intercompany',
      'Other',
    ];
    const found = [...new Set(items.map((i) => i.accountSubType))];
    const customerTypes = [
      ...preferredOrder.filter((t) => found.includes(t)),
      ...found.filter((t) => !preferredOrder.includes(t)), // any unexpected types go at end
    ];
    const rowMap = new Map<string, CredentialPriceRow>();

    for (const item of items) {
      const key = `${item.productDesc}__${item.skuCode}`;
      if (!rowMap.has(key)) {
        rowMap.set(key, { type: item.productDesc, sku: item.skuCode });
      }
      const row = rowMap.get(key)!;
      row[item.accountSubType] = `$${item.credentialPrice.toFixed(2)}`;
    }

    return { customerTypes, rows: Array.from(rowMap.values()) };
  }
}
